import { ADDY_LIST_PRICES, CLIENT_TYPES, ROI_TASKS } from "./assumptions";
import { toAmount } from "./calculators";

export const EMPTY_ROI_STATE = { pilotId: "", edits: {}, tasks: {} };

/** A CLIENT_TYPES key for HubSpot's "Type of Company" value, or null. */
export function clientTypeFromHubSpot(companyType) {
  return CLIENT_TYPES.find((type) => type.hubspotValue === companyType)?.key || null;
}

/**
 * Starting ROI inputs: HubSpot facts about the picked pilot, its Addy
 * organization name and Addy's list prices. The client's own numbers start
 * empty, because only the client can give them.
 *
 * @returns {{ values: object, sources: object }}
 */
export function buildRoiBase({ pilot, organizationName }) {
  const values = {
    clientType: "",
    clientName: "",
    loansPerMonth: "",
    hourlyCost: "",
    turnTimeDaysSaved: "",
    pricePerLoan: ADDY_LIST_PRICES.pricePerLoan,
    pricePerSeat: ADDY_LIST_PRICES.pricePerSeat,
    seats: "",
  };
  const sources = { pricePerLoan: "list", pricePerSeat: "list" };
  if (!pilot) {
    return { values, sources };
  }

  const clientType = clientTypeFromHubSpot(pilot.companyType);
  if (clientType) {
    values.clientType = clientType;
    sources.clientType = "hubspot";
  }
  if (pilot.loanOfficers > 0) {
    values.seats = pilot.loanOfficers;
    sources.seats = "hubspot";
  }
  values.clientName = organizationName || pilot.name;
  sources.clientName = organizationName ? "db" : "hubspot";

  return { values, sources };
}

/**
 * What the ROI calculator shows: the base, overridden by what the user typed.
 * The pricing model follows the client type until someone picks one.
 *
 * @returns {{ values: object, sources: object }}
 */
export function resolveRoiInputs({ base, state }) {
  const values = { preparedBy: "", ...base.values, ...state.edits };
  const sources = { ...base.sources };
  Object.keys(state.edits).forEach((key) => {
    sources[key] = "edited";
  });
  if (!("pricingModel" in state.edits)) {
    values.pricingModel =
      CLIENT_TYPES.find((type) => type.key === values.clientType)?.pricingModel || "perLoan";
  }
  values.tasks = Object.fromEntries(
    ROI_TASKS.map((task) => [
      task.key,
      {
        minutesToday: state.tasks[task.key]?.minutesToday ?? "",
        minutesWithAddy: state.tasks[task.key]?.minutesWithAddy ?? "",
      },
    ]),
  );

  return { values, sources };
}

/** The inputs `calculateRoi` expects, tasks in display order. */
export function toRoiInputs(values) {
  return {
    ...values,
    tasks: ROI_TASKS.map((task) => ({ key: task.key, ...values.tasks[task.key] })),
  };
}

/** What still has to be typed in before the ROI means anything. */
export function missingRoiInputs(values, result) {
  const missing = [];
  if (!toAmount(values.loansPerMonth)) {
    missing.push("loans closed per month");
  }
  if (!toAmount(values.hourlyCost)) {
    missing.push("staff cost per hour");
  }
  if (!(result.minutesSavedPerLoan > 0)) {
    missing.push("minutes per task, today and with Addy");
  }
  const price = values.pricingModel === "perSeat" ? values.pricePerSeat : values.pricePerLoan;
  if (!toAmount(price)) {
    missing.push("Addy's price");
  }
  if (values.pricingModel === "perSeat" && !toAmount(values.seats)) {
    missing.push("seats");
  }

  return missing;
}
